import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";

import { HydrovacTicket } from "../../interface/api/HydrovacTicket";
import { hydrovacTicketNumber } from "../../utils/parser";
import { ContainmentPad } from "../../interface/api/ContainmentPad";
import storageAreaService from "../../services/storageAreaService";
import Button from "../shared/Button";
import { useDialogStyles } from "../shared/dialog/styles";
import { HydrovacHeadspaceDialogProps } from "../../interface/dialog/HydrovacHeadspaceDialogProps";

const inspectionItems = [
  { field: "inspect1_OrganicMaterial", label: "Organic Material" },
  { field: "inspect2_Insects", label: "Insects" },
  { field: "inspect3_UnusualOdours", label: "Unusual Odours" },
  { field: "inspect4_OtherMaterial", label: "Other Material" },
  { field: "inspect5_HydrocarbonSheen", label: "Hydrocarbon Sheen" },
  { field: "inspect6_HeadspaceAbove200", label: "Headspace Above 200" },
];

const requiredTextFields: (keyof HydrovacTicket)[] = [
  "headspaceReading",
  "visualInspectionResult",
  "containmentBay",
];

const HydrovacHeadspaceDialog = ({
  open,
  ticket,
  onClose,
  onEnterHeadspace,
}: HydrovacHeadspaceDialogProps) => {
  const classes = useDialogStyles();
  const [editedTicket, setEditedTicket] = useState<HydrovacTicket | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [padName, setPadName] = useState<string>("");

  useEffect(() => {
    if (ticket) {
      setEditedTicket({ ...ticket });
      setSubmitted(false);
    }
  }, [ticket]);

  // Resolve the assigned containment pad name (read-only; chosen at receive).
  useEffect(() => {
    if (open && ticket?.containmentPadId) {
      storageAreaService.getContainmentPad(ticket.containmentPadId).then((r) => {
        if (r.ok && r.data) setPadName((r.data as ContainmentPad).name ?? "");
      });
    } else {
      setPadName("");
    }
  }, [open, ticket?.id]);

  if (!editedTicket) return null;

  const updateField = (field: keyof HydrovacTicket, value: any) => {
    setEditedTicket({ ...editedTicket, [field]: value });
  };

  const isFieldEmpty = (field: keyof HydrovacTicket) => {
    const val = (editedTicket as any)[field];
    return val === null || val === undefined || val === "";
  };

  const handleSave = () => {
    setSubmitted(true);
    const missing = requiredTextFields.some((f) => isFieldEmpty(f));
    if (missing) {
      toast.error(
        "Headspace reading, visual inspection, and containment bay are required"
      );
      return;
    }
    onEnterHeadspace(editedTicket);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        Enter Headspace — Ticket #{hydrovacTicketNumber(editedTicket)}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
          Field Assessment
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Headspace Reading"
              required
              error={submitted && isFieldEmpty("headspaceReading")}
              helperText={submitted && isFieldEmpty("headspaceReading") ? "Required" : ""}
              value={editedTicket.headspaceReading ?? ""}
              onChange={(e) =>
                updateField("headspaceReading", e.target.value || null)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Visual Inspection Result"
              required
              error={submitted && isFieldEmpty("visualInspectionResult")}
              helperText={submitted && isFieldEmpty("visualInspectionResult") ? "Required" : ""}
              value={editedTicket.visualInspectionResult ?? ""}
              onChange={(e) => updateField("visualInspectionResult", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Containment Pad"
              value={padName}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              helperText="Assigned at receive"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              variant="outlined"
              label="Containment Bay"
              required
              error={submitted && isFieldEmpty("containmentBay")}
              helperText={submitted && isFieldEmpty("containmentBay") ? "Required" : ""}
              value={editedTicket.containmentBay ?? ""}
              onChange={(e) =>
                updateField(
                  "containmentBay",
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            >
              {[1, 2, 3, 4].map((bay) => (
                <MenuItem key={bay} value={bay}>
                  {bay}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}>
          Inspection Checklist
        </Typography>
        <Grid container spacing={1}>
          {inspectionItems.map(({ field, label }) => (
            <Grid item xs={12} sm={6} key={field}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={(editedTicket as any)[field] ?? false}
                    onChange={(e) =>
                      updateField(field as keyof HydrovacTicket, e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={label}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <div className={classes.actionButton}>
          <Button
            label="Cancel"
            onClick={onClose}
            color="default"
            variant="contained"
            type="button"
          />
        </div>
        <div className={classes.actionButton}>
          <Button
            label="Save"
            onClick={handleSave}
            color="primary"
            variant="contained"
            type="button"
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default HydrovacHeadspaceDialog;
